import {
  areTimeRangeInputValuesEqual,
  type TimeRangeInputValue,
  type TimeRangeValue,
  timeRangeInputValueToLegacyTimeRange,
} from '@chronosphereio/core';
import { usePrevious, useUpdateEffect } from '@react-hookz/web';
import { isEqual } from 'lodash';
import { useState } from 'react';
import { type TimeRangeProviderProps } from './TimeRangeProviderV2';
import { type TimeRangeProviderApi } from './time-range-api';

export interface BackwardsCompatibilityProps {
  /**
   * Enables re-initializing the time range based on the defaultTimeRange passed in via props
   */
  enableReinitialize?: boolean;
  /**
   * Key used to force an update and recompute internal state of TimeRangeProvider.
   * Useful when initial relative time range needs to update to 'now' (ex: set recomputeKey to absoluteTimeRange.end).
   * Better for performance since it does not cause children to unmount compared to using a React key.
   * The recomputeKey is appended to the refreshKey used in our TanStack Query implementation and forces data queries to rerun.
   */
  recomputeKey?: string;
  /**
   * Callback function that is triggered whenever the `timeRange` changes.
   * Can be used to synchronize nested `TimeRangeProvider`s and their parent components.
   * @param timeRange The new time range value
   */
  onTimeRangeChange?: (timeRange: TimeRangeValue) => void;
}

/**
 * Adds support for a few props from the v1 provider that we probably want to get rid of long-term.
 */
export function useBackwardsCompatibility(api: TimeRangeProviderApi, props: TimeRangeProviderProps) {
  const { timeRange, setTimeRange, refresh } = api;
  const { onTimeRangeChange } = props;

  // This implementation is copied from the v1 provider
  const prevTimeRange = usePrevious(timeRange);
  useUpdateEffect(() => {
    if (onTimeRangeChange && !isEqual(timeRange, prevTimeRange)) {
      onTimeRangeChange(timeRangeInputValueToLegacyTimeRange(timeRange));
    }
  }, [timeRange, onTimeRangeChange, prevTimeRange]);

  // This is to handle a couple use cases where TimeRangeProviders are nested and consumers want to force an internal
  // state update by changing the props they pass in. For example, the `TimeOverrideProvider` for dashboards or the
  // `PanelTimeRangeProvider` used in notebooks
  const [prevProps, setPrevProps] = useState(props);
  const stateChangeFromProps = getStateChangeFromProps(prevProps, props);
  if (stateChangeFromProps !== false) {
    setPrevProps(props);

    // If we got 'true' that basically means only the recomputeKey changed so we want to refresh, otherwise the
    // defaultTimeRange changed and we need to update our time range to match
    if (stateChangeFromProps === true) {
      refresh();
    } else {
      setTimeRange(stateChangeFromProps);
    }
  }
}

// A time range if we need to update the time range to track the default, or a boolean indicating whether we need to refresh
type StateChangeFromProps = TimeRangeInputValue | boolean;

// This helper function basically checks to see if some external prop changes should force an internal state update
// in the provider (see comments below)
function getStateChangeFromProps(
  previousProps: TimeRangeProviderProps,
  currentProps: TimeRangeProviderProps
): StateChangeFromProps {
  // These state changes only apply to nested (in-memory) providers
  if (currentProps.enableUrlParams === true) {
    return false;
  }

  // If the defaultTimeRange prop changes, we want to update state to track that value (which essentially is a refresh
  // so we can ignore whether the recomputeKey also changed)
  if (
    currentProps.enableReinitialize === true &&
    previousProps.defaultTimeRange !== undefined &&
    currentProps.defaultTimeRange !== undefined &&
    areTimeRangeInputValuesEqual(previousProps.defaultTimeRange, currentProps.defaultTimeRange) === false
  ) {
    return currentProps.defaultTimeRange;
  }

  // If the recomputeKey changes (independent of defaultTimeRange), we want to force a refresh
  if (
    previousProps.recomputeKey !== undefined &&
    currentProps.recomputeKey !== undefined &&
    previousProps.recomputeKey !== currentProps.recomputeKey
  ) {
    return true;
  }

  // Otherwise, we don't need to do any state updates
  return false;
}
